import { NextRequest, NextResponse } from "next/server";
import {
	locales,
	isValidLocale,
	defaultLocale,
	getRegionalLocaleForCountry,
	getRegionalSpec,
	type Locale,
} from "@/config/locale";
import { GEO_PREF_COOKIE, LEGACY_GEO_PREF_COOKIE, parseGeoPrefCookieValue } from "@/lib/geo/geo-pref";
import {
	AFFILIATE_SESSION_COOKIE_NAME,
	AFFILIATE_PORTAL_LOCALE_COOKIE_NAME,
	ADMIN_SESSION_COOKIE_NAME,
	verifyEdgeSessionToken,
} from "@/lib/affiliate/session";
import { LOYALTY_FRIEND_VOUCHER_COOKIE_NAME } from "@/lib/loyalty/referral";
import { resolveMailLinkHopTarget } from "@/lib/email/mail-link-origin";
import {
	BACKUP_STOREFRONT_HOST,
	DOMAIN_TRANSFER_START_PATH,
	DOMAIN_TRANSFER_TARGET_HEADER,
	MAIN_STOREFRONT_ORIGIN,
	isDomainTransferNavigation,
	isStorefrontHost,
} from "@/lib/http/retired-domain";
import { createRateLimiter } from "@/lib/rate-limit";
import { agentCanaryResponse, matchAgentCanary } from "@/lib/security/agent-canary";
import { isCrawlerUserAgent } from "@/lib/seo/crawler-ua";
import { applyPublicDiscoveryHeaders, isPublicDiscoveryPath } from "@/lib/agent/discovery";
import { isSitemapChildPath } from "@/lib/sitemap/sections";
import {
	isAgentMarkdownEnabled,
	isMarkdownPilotPath,
	isMarkdownRequestExcluded,
	parseMarkdownTarget,
	prefersMarkdown,
} from "@/lib/agent/markdown-negotiation";
import {
	buildCampaignLandingUrl,
	CAMPAIGN_COOKIE_MAX_AGE,
	CAMPAIGN_COOKIE_NAME,
	CAMPAIGN_KILL_PARAM,
	getQrCampaign,
	getQrCampaignByShortPath,
	serializeCampaignCookie,
	type QrCampaignKey,
} from "@/lib/campaigns/qr-campaigns";

/**
 * Valid channel slugs — anything else after /{locale}/ that is not a known
 * non-channel path (affiliate, admin, checkout) gets 308-redirected into
 * the default channel (a regional locale's primary channel, see
 * channelForLocalePath).  This prevents Google from indexing nonsense URLs
 * like /de/favorites or /it/about/guides/bpc-157 where the second segment
 * is incorrectly treated as a channel.
 *
 * Uses `||` (not `??`) so empty-string env vars fall through to the default.
 * NEXT_PUBLIC_* vars are inlined at build time — if absent during build, they
 * resolve to `undefined`; if present but empty, to `""`.  Both must fall through.
 */
const DEFAULT_CHANNEL = process.env.NEXT_PUBLIC_DEFAULT_CHANNEL || "eu";

/**
 * Slugs that exist only as products (not real content categories) but are linkable
 * as `/categories/<slug>`. 308-redirect them to the canonical product page so the
 * empty-category soft-404 never surfaces and search demand consolidates on the SKU.
 */
const CATEGORY_TO_PRODUCT_REDIRECTS: Record<string, string> = {
	"bac-water": "bac-water-10ml",
};
const VALID_CHANNELS = new Set([
	...(process.env.NEXT_PUBLIC_SITEMAP_CHANNELS || DEFAULT_CHANNEL)
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean),
	DEFAULT_CHANNEL, // always include so the default channel is never rejected
]);

/**
 * Channel inserted when a locale path names no valid channel (`/es-mx`, `/de/favorites`):
 * a regional locale's primary channel (es-mx → us), the default channel otherwise. Falls back
 * to the default when the primary channel is not served here, so `/es-mx/us` can never be
 * rejected again as an invalid channel segment (redirect loop).
 */
function channelForLocalePath(locale: Locale): string {
	const primaryChannel = getRegionalSpec(locale)?.primaryChannel;
	return primaryChannel && VALID_CHANNELS.has(primaryChannel) ? primaryChannel : DEFAULT_CHANNEL;
}

/** Countries in North, Central, South America + Caribbean → "us" channel */
export const AMERICAS_COUNTRIES = new Set([
	// North America
	"US",
	"CA",
	"MX",
	// Central America
	"GT",
	"BZ",
	"SV",
	"HN",
	"NI",
	"CR",
	"PA",
	// Caribbean
	"CU",
	"JM",
	"HT",
	"DO",
	"TT",
	"BB",
	"AG",
	"BS",
	"DM",
	"GD",
	"KN",
	"LC",
	"VC",
	"PR",
	"VI",
	// South America
	"BR",
	"AR",
	"CL",
	"CO",
	"PE",
	"VE",
	"EC",
	"BO",
	"PY",
	"UY",
	"GY",
	"SR",
]);

/**
 * Italian, Finnish, Swedish, Romanian: always default to EU channel, even when
 * CF-IPCountry is in the Americas (VPN, expats, travel). Matching home countries
 * force EU even if locale detection falls through to English.
 */
const EU_CHANNEL_DEFAULT_LOCALES = new Set(["it", "fi", "sv", "ro"]);
export const EU_CHANNEL_DEFAULT_COUNTRIES = new Set(["IT", "SM", "VA", "FI", "SE", "RO"]);

/**
 * Cloudflare CF-IPCountry → storefront locale.
 * Only unambiguous single-language countries are mapped here.
 * Ambiguous countries (CH, BE, LU) fall through to Accept-Language.
 */
const COUNTRY_TO_LOCALE: Record<string, string> = {
	// German-speaking
	DE: "de",
	AT: "de",
	LI: "de",
	// Italian
	IT: "it",
	SM: "it",
	// Spanish-speaking
	ES: "es",
	MX: "es-mx",
	AR: "es",
	CL: "es",
	CO: "es",
	PE: "es",
	VE: "es",
	EC: "es",
	BO: "es",
	PY: "es",
	UY: "es",
	CU: "es",
	DO: "es",
	GT: "es",
	SV: "es",
	HN: "es",
	NI: "es",
	CR: "es",
	PA: "es",
	PR: "es",
	GQ: "es",
	// French-speaking
	FR: "fr",
	MC: "fr",
	SN: "fr",
	CI: "fr",
	ML: "fr",
	BF: "fr",
	NE: "fr",
	TD: "fr",
	CF: "fr",
	CG: "fr",
	GA: "fr",
	DJ: "fr",
	KM: "fr",
	// Dutch-speaking
	NL: "nl",
	// Portuguese-speaking
	PT: "pt",
	BR: "pt-br",
	AO: "pt",
	MZ: "pt",
	// Polish
	PL: "pl",
	// Swedish
	SE: "sv",
	// Danish
	DK: "da",
	// Greek
	GR: "el",
	CY: "el",
	// South Slavic markets (Sprachwelle 1)
	SI: "sl",
	RS: "sr",
	BA: "bs",
	MK: "mk",
	// Baltic markets (Sprachwelle 1b). EE is Estonia's ISO country code.
	LT: "lt",
	LV: "lv",
	EE: "et",
	// English-speaking
	US: "en",
	GB: "en",
	IE: "en",
	AU: "en",
	NZ: "en",
	CA: "en",
	JM: "en",
	TT: "en",
	BB: "en",
	AG: "en",
	BS: "en",
	GY: "en",
	// Czech
	CZ: "cs",
	// Slovak
	SK: "sk",
	// Hungarian
	HU: "hu",
	// Romanian
	RO: "ro",
	// Bulgarian
	BG: "bg",
	// Croatian
	HR: "hr",
	// Norwegian
	NO: "nb",
	// Finnish
	FI: "fi",
	// Turkish
	TR: "tr",
};

/** Paths directly under [locale] that are NOT channels and bypass channel validation. */
const LOCALE_DIRECT_PATHS = new Set(["affiliate", "admin", "checkout", "vorkasse"]);

/**
 * Allow-list of `/.well-known/*` files that must resolve (not 404). Request-invariant,
 * so it lives at module scope rather than being reallocated on every request.
 */
const WELL_KNOWN_ALLOW_LIST = new Set(["apple-developer-merchantid-domain-association"]);

/**
 * Page routes cannot use `withRateLimit` (that guard wraps API handlers only).
 * The /vorkasse/ prefix renders an IBAN against an enumerable Saleor order GID,
 * so it gets its own edge-side cap.
 */
const vorkassePageLimiter = createRateLimiter({ maxRequests: 30, windowMs: 60_000 });

/**
 * One URL per page (GAP-04): GET/HEAD page paths with a trailing slash get exactly one 308
 * to the slash-free form; the query is preserved. Built from `new URL(request.url)` on
 * purpose: `request.nextUrl.clone()` remembers the original trailing slash and would add it
 * back when formatting. API, `_next` and `.well-known` never reach this branch; file-like
 * last segments keep their own handling.
 */
function trailingSlashRedirect(request: NextRequest): NextResponse | null {
	const { pathname } = request.nextUrl;
	if (pathname === "/" || !pathname.endsWith("/")) return null;
	if (request.method !== "GET" && request.method !== "HEAD") return null;
	const trimmed = pathname.replace(/\/+$/, "") || "/";
	const lastSegment = trimmed.slice(trimmed.lastIndexOf("/") + 1);
	if (lastSegment.includes(".")) return null;
	const target = new URL(request.url);
	target.pathname = trimmed;
	return NextResponse.redirect(target, 308);
}

/**
 * Middleware for locale detection and redirection.
 *
 * - If path starts with a valid locale → continue, set x-locale header
 * - If no locale in path → detect target → 307 + no-store for humans, fixed 308 to /de/eu for crawlers
 * - Skips /_next, /api, static files
 */
async function handleMiddlewareRequest(request: NextRequest) {
	const { pathname } = request.nextUrl;

	// Skip API routes entirely (matcher should exclude, but be explicit)
	if (pathname.startsWith("/api/")) {
		return NextResponse.next();
	}

	// Historical provider callbacks keep their direct API entry. Browser navigation
	// on the retired host moves to the main shop without discarding its guest cart.
	if (
		isStorefrontHost(request.headers.get("host"), BACKUP_STOREFRONT_HOST) &&
		isDomainTransferNavigation(request)
	) {
		const hasCart = ["eu", "us"].some((channel) =>
			Boolean(request.cookies.get(`checkoutId-${channel}`)?.value),
		);
		const target = new URL(MAIN_STOREFRONT_ORIGIN);
		target.pathname = pathname;
		target.search = request.nextUrl.search;
		const response =
			hasCart && request.method === "GET"
				? (() => {
						const start = request.nextUrl.clone();
						start.pathname = DOMAIN_TRANSFER_START_PATH;
						start.search = "";
						const headers = new Headers(request.headers);
						headers.set(DOMAIN_TRANSFER_TARGET_HEADER, `${pathname}${request.nextUrl.search}`);
						return NextResponse.rewrite(start, { request: { headers } });
					})()
				: NextResponse.redirect(target, 307);
		response.headers.set("Cache-Control", "private, no-store");
		response.headers.set("Referrer-Policy", "no-referrer");
		return response;
	}

	// Mail-link-domain hop, only active while EMAIL_LINK_ORIGIN names a separate
	// deliverable link domain (unset in prod since 2026-09-29; old
	// bergdorfbioscience.com links 308 at Cloudflare). Clicks land here exactly once and immediately 308 to
	// the canonical shop domain — customers never browse on the secondary domain.
	// /api stays exempt (matcher + skip above): the RFC 8058 one-click
	// unsubscribe POST must not run through a redirect.
	const mailLinkHopTarget = resolveMailLinkHopTarget(request.nextUrl, request.headers.get("host"));
	if (mailLinkHopTarget) {
		return NextResponse.redirect(mailLinkHopTarget, 308);
	}

	// Public machine-readable resources have their own root handlers and never
	// vary with locale/country/session cookies.
	if (isPublicDiscoveryPath(pathname)) return NextResponse.next();

	// XML sitemap + child sitemaps must not get a locale redirect.
	// The XSL was removed on 2026-10-25: /sitemap.xsl keeps no locale redirect and answers 404.
	// Atom feed — root-level route handler, not locale-prefixed
	if (
		pathname === "/sitemap.xml" ||
		pathname === "/sitemap.xsl" ||
		pathname === "/feed.xml" ||
		isSitemapChildPath(pathname)
	) {
		return NextResponse.next();
	}

	// /{locale}/sitemap.xml|xsl|feed.xml is not a static file — Next would return HTML or 404. Canonical: root only.
	const misplacedSitemap = pathname.split("/").filter(Boolean);
	if (
		misplacedSitemap.length === 2 &&
		locales.includes(misplacedSitemap[0] as (typeof locales)[number]) &&
		(misplacedSitemap[1] === "sitemap.xml" ||
			misplacedSitemap[1] === "sitemap.xsl" ||
			misplacedSitemap[1] === "feed.xml")
	) {
		const canonical = request.nextUrl.clone();
		canonical.pathname = `/${misplacedSitemap[1]}`;
		return NextResponse.redirect(canonical, 308);
	}

	// /robots.txt — a text route preserves Content-Signal alongside crawl rules.
	// /llms.txt — AI-friendly Markdown site overview; served by `app/llms.txt/route.ts`.
	// Matcher does not exclude .txt (IndexNow uses /{key}.txt via rewrite below).
	if (pathname === "/robots.txt" || pathname === "/llms.txt") {
		return NextResponse.next();
	}

	// /.well-known/* — Apple Pay domain verification must resolve byte-identically and without a
	// locale redirect. Everything else under /.well-known/ is a scanner probe: answer 404 here
	// rather than letting it fall through to /_not-found, which under cacheComponents throws
	// "Invalid revalidate configuration 0 < 1" -> 500.
	if (pathname.startsWith("/.well-known/")) {
		const name = pathname.slice("/.well-known/".length);
		if (WELL_KNOWN_ALLOW_LIST.has(name)) return NextResponse.next();
		return new NextResponse(null, { status: 404 });
	}

	// IndexNow key file (Option 1): https://{host}/{key}.txt — must bypass locale redirect
	const indexNowKey = process.env.INDEXNOW_KEY;
	if (indexNowKey && pathname === `/${indexNowKey}.txt`) {
		return NextResponse.rewrite(new URL("/api/indexnow/key", request.url));
	}

	// Redirect /partners → /{locale}/{channel}/partners (avoids 404: "partners" is not a valid channel)
	if (pathname === "/partners" || pathname.startsWith("/partners/")) {
		const detectedLocale = detectLocale(request);
		const newUrl = request.nextUrl.clone();
		newUrl.pathname = `/${detectedLocale}/${detectChannel(request, detectedLocale)}${pathname}`;
		return NextResponse.redirect(newUrl, 308);
	}

	// ── QR-Kampagnen-Kurzlink (/go/<key>) ──
	// Muss vor `const pathSegments` und vor der Locale-Erkennung laufen: /go/tides traegt
	// kein Locale-Praefix und wuerde sonst vom locale-losen Block auf /{locale}/{channel}/go/tides
	// umgeschrieben, wo es 404t.
	//
	// 307, NICHT 308: Browser cachen 308 dauerhaft. Der Kampagnen-Zielpfad muss aenderbar
	// bleiben, ohne den gedruckten QR-Code zu ersetzen.
	//
	// Eingehende Query-Parameter werden verworfen. Die Ziel-URL wird ausschliesslich aus
	// `landingPath` + `utm` der Registry gebaut, damit der Code deterministisch bleibt und
	// niemand fremde UTM-Werte oder Pfade anhaengen kann.
	//
	// Bei `enabled: false` (Kill-Switch) geht es ohne UTM auf den nackten landingPath.
	// Unbekannte /go/* Pfade fallen unveraendert durch, es gibt bewusst keinen Sonderfall.
	const qrShortLink = getQrCampaignByShortPath(pathname);
	if (qrShortLink) {
		const target = qrShortLink.campaign.enabled
			? buildCampaignLandingUrl(qrShortLink.campaign)
			: qrShortLink.campaign.landingPath;
		return NextResponse.redirect(new URL(target, request.url), 307);
	}

	// Redirect /{locale}/partners → /{locale}/{channel}/partners (e.g. /en/partners → /en/eu/partners,
	// /es-mx/partners → /es-mx/us/partners)
	const pathSegments = pathname.split("/").filter(Boolean);
	if (
		pathSegments.length === 2 &&
		locales.includes(pathSegments[0] as (typeof locales)[number]) &&
		pathSegments[1] === "partners"
	) {
		const newUrl = request.nextUrl.clone();
		newUrl.pathname = `/${pathSegments[0]}/${channelForLocalePath(pathSegments[0] as Locale)}/partners`;
		return NextResponse.redirect(newUrl, 308);
	}
	if (
		pathSegments.length >= 3 &&
		locales.includes(pathSegments[0] as (typeof locales)[number]) &&
		pathSegments[1] === "partners"
	) {
		const newUrl = request.nextUrl.clone();
		newUrl.pathname = `/${pathSegments[0]}/${channelForLocalePath(pathSegments[0] as Locale)}/partners/${pathSegments.slice(2).join("/")}`;
		return NextResponse.redirect(newUrl, 308);
	}

	// Check if path already has a locale prefix
	const pathnameLocale = locales.find(
		(locale) => pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`,
	);

	if (pathnameLocale) {
		// ── Channel validation ──
		// URL structure: /{locale}/{channel}/...
		// Reject invalid channel values to prevent Google from indexing gibberish URLs.
		const pathAfterLocale = pathname.slice(`/${pathnameLocale}`.length); // e.g. "/favorites" or "/about/guides/..."
		const secondSegment = pathAfterLocale.split("/").filter(Boolean)[0]; // e.g. "favorites", "eu", "affiliate"
		if (!secondSegment || (!VALID_CHANNELS.has(secondSegment) && !LOCALE_DIRECT_PATHS.has(secondSegment))) {
			// Bare locale (/de) or an invalid channel segment -> 308 to the locale's primary channel
			// (es-mx: us, every other locale: the default channel).
			// new URL(request.url), not nextUrl.clone(): NextURL would re-add an original trailing slash.
			const newUrl = new URL(request.url);
			newUrl.pathname =
				`/${pathnameLocale}/${channelForLocalePath(pathnameLocale)}${pathAfterLocale}`.replace(/\/+$/, "");
			return NextResponse.redirect(newUrl, 308);
		}

		const slashRedirect = trailingSlashRedirect(request);
		if (slashRedirect) return slashRedirect;

		if (pathAfterLocale === "/vorkasse" || pathAfterLocale.startsWith("/vorkasse/")) {
			const ip = request.headers.get("cf-connecting-ip") || request.headers.get("x-real-ip") || "unknown";
			if (!vorkassePageLimiter(`vorkasse-page:${ip}`).allowed) {
				return new NextResponse("Too Many Requests", {
					status: 429,
					headers: { "Retry-After": "60" },
				});
			}
		}

		// ── Legacy calculator URL redirects (301) ──
		// /{locale}/{channel}/peptide-calculator/content/{slug} → /{locale}/{channel}/peptide-calculator/{slug}
		// /{locale}/{channel}/calculator → /{locale}/{channel}/peptide-calculator
		// Must run before the /reports check and before affiliate/admin route guards.
		const afterLocaleSegments = pathAfterLocale.split("/").filter(Boolean);

		// /{locale}/{channel}/peptide-calculator/content/{slug} → /{locale}/{channel}/peptide-calculator/{slug}
		if (
			afterLocaleSegments.length >= 4 &&
			VALID_CHANNELS.has(afterLocaleSegments[0]) &&
			afterLocaleSegments[1] === "peptide-calculator" &&
			afterLocaleSegments[2] === "content" &&
			afterLocaleSegments[3]
		) {
			const newUrl = request.nextUrl.clone();
			newUrl.pathname = `/${pathnameLocale}/${afterLocaleSegments[0]}/peptide-calculator/${afterLocaleSegments.slice(3).join("/")}`;
			return NextResponse.redirect(newUrl, 301);
		}

		// /{locale}/{channel}/calculator → /{locale}/{channel}/peptide-calculator (query string preserved via clone)
		if (
			afterLocaleSegments.length === 2 &&
			VALID_CHANNELS.has(afterLocaleSegments[0]) &&
			afterLocaleSegments[1] === "calculator"
		) {
			const newUrl = request.nextUrl.clone();
			newUrl.pathname = `/${pathnameLocale}/${afterLocaleSegments[0]}/peptide-calculator`;
			return NextResponse.redirect(newUrl, 301);
		}

		if (
			afterLocaleSegments.length >= 2 &&
			VALID_CHANNELS.has(afterLocaleSegments[0]) &&
			afterLocaleSegments[1] === "reports"
		) {
			const newUrl = request.nextUrl.clone();
			newUrl.pathname = `/${pathnameLocale}/${afterLocaleSegments[0]}/`;
			return NextResponse.redirect(newUrl, 308);
		}

		// Product-only slugs linked as /categories/<slug> -> 308 to the canonical product.
		if (
			afterLocaleSegments.length === 3 &&
			VALID_CHANNELS.has(afterLocaleSegments[0]) &&
			afterLocaleSegments[1] === "categories" &&
			CATEGORY_TO_PRODUCT_REDIRECTS[afterLocaleSegments[2]]
		) {
			const newUrl = request.nextUrl.clone();
			newUrl.pathname = `/${pathnameLocale}/${afterLocaleSegments[0]}/products/${CATEGORY_TO_PRODUCT_REDIRECTS[afterLocaleSegments[2]]}`;
			return NextResponse.redirect(newUrl, 308);
		}

		const requestHeaders = new Headers(request.headers);
		requestHeaders.set("x-pathname", pathname);
		// Independent/streamed renders may not share setRequestLocale's React cache.
		// Give next-intl the validated route locale at the request boundary as well.
		requestHeaders.set("x-next-intl-locale", pathnameLocale);
		const response = NextResponse.next({ request: { headers: requestHeaders } });
		response.headers.set("x-locale", pathnameLocale);

		const referralCode = request.nextUrl.searchParams.get("ref");
		if (referralCode) {
			const cleanPath = buildCleanPath(request.nextUrl);
			const trackUrl = new URL("/api/affiliate/track", request.url);
			trackUrl.searchParams.set("ref", referralCode);
			trackUrl.searchParams.set("next", cleanPath);
			// Forward UTM params
			for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
				const val = request.nextUrl.searchParams.get(key);
				if (val) trackUrl.searchParams.set(key, val);
			}
			return NextResponse.redirect(trackUrl, 307);
		}

		// Loyalty referral link handler
		const loyaltyRefCode = request.nextUrl.searchParams.get("lref");
		if (loyaltyRefCode) {
			const cleanUrl = request.nextUrl.clone();
			cleanUrl.searchParams.delete("lref");

			const redirectResponse = NextResponse.redirect(cleanUrl, 307);

			// Set loyalty_ref cookie (30 days)
			// Pass raw JSON — Next.js ResponseCookies.set() applies encodeURIComponent internally.
			const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
			const cookieValue = JSON.stringify({ referralCode: loyaltyRefCode, expiresAt });
			redirectResponse.cookies.set("loyalty_ref", cookieValue, {
				maxAge: 30 * 24 * 60 * 60,
				path: "/",
				httpOnly: false, // needs to be readable by client for checkout
				sameSite: "lax",
			});

			// Neuer Werber → alter BRF-Gutschein wäre für den vorherigen Link; zurücksetzen
			redirectResponse.cookies.set(LOYALTY_FRIEND_VOUCHER_COOKIE_NAME, "", {
				maxAge: 0,
				path: "/",
			});

			return redirectResponse;
		}

		// ── QR-Kampagnen-Cookie ──
		// Erkennungsschluessel ist utm_campaign selbst, kein zusaetzlicher Parameter.
		// Unbekannte Werte werden ignoriert, ebenso `enabled: false` (getQrCampaign gibt
		// dann null zurueck) und der Session-Kill-Switch ?tides=0.
		//
		// Das Cookie wird bewusst auf das oben (bei `const response = NextResponse.next(...)`)
		// gebaute `response`-Objekt gesetzt. Ein neues NextResponse.next() ist hier verboten:
		// es wuerde den umgeschriebenen x-pathname Request-Header und den x-locale
		// Response-Header verlieren und, da es zurueckgegeben werden muesste, Affiliate-Auth,
		// Admin-Guard und den restlichen Middleware-Lauf fuer saemtlichen Kampagnen-Traffic
		// ueberspringen. Deshalb auch: kein return und kein Redirect, der Block faellt in das
		// bestehende `return response` am Ende des Zweigs durch.
		//
		// utm_campaign bleibt anders als lref in der URL stehen, weil PostHog den Parameter
		// auf der Landing-URL sehen muss.
		//
		// Nutzlast ist ausschliesslich der Kampagnen-Schluessel, niemals der Voucher-Code:
		// httpOnly ist false, damit das Banner clientseitig lesen kann, und ein Code im
		// Cookie liesse sich per document.cookie gegen einen beliebigen fremden Code
		// austauschen, den der Server dann automatisch auf den Checkout legt.
		const campaignParam = request.nextUrl.searchParams.get("utm_campaign");
		if (
			campaignParam &&
			getQrCampaign(campaignParam) &&
			request.nextUrl.searchParams.get(CAMPAIGN_KILL_PARAM) !== "0"
		) {
			// getQrCampaign hat den Wert gegen die Registry validiert; der Cast ist deshalb
			// sicher und noetig, weil die Signatur bewusst `string` entgegennimmt.
			response.cookies.set(CAMPAIGN_COOKIE_NAME, serializeCampaignCookie(campaignParam as QrCampaignKey), {
				maxAge: CAMPAIGN_COOKIE_MAX_AGE,
				path: "/",
				httpOnly: false, // muss vom Banner clientseitig lesbar sein
				sameSite: "lax",
			});
		}

		if (
			pathname.startsWith(`/${pathnameLocale}/affiliate`) &&
			!isPublicAffiliatePath(pathname, pathnameLocale)
		) {
			const affiliateResponse = await protectRoute({
				request,
				cookieName: AFFILIATE_SESSION_COOKIE_NAME,
				expectedRole: "affiliate",
				redirectTo: `/${pathnameLocale}/affiliate/login`,
			});

			if (affiliateResponse) {
				return affiliateResponse;
			}

			const preferredPortalLocale = request.cookies.get(AFFILIATE_PORTAL_LOCALE_COOKIE_NAME)?.value;
			if (
				preferredPortalLocale &&
				isValidLocale(preferredPortalLocale) &&
				preferredPortalLocale !== pathnameLocale
			) {
				const affiliateTail = pathname.slice(`/${pathnameLocale}/affiliate`.length) || "/dashboard";
				const target = request.nextUrl.clone();
				target.pathname = `/${preferredPortalLocale}/affiliate${affiliateTail}`;
				return NextResponse.redirect(target, 307);
			}
		}

		if (
			pathname.startsWith(`/${pathnameLocale}/admin`) &&
			!pathname.startsWith(`/${pathnameLocale}/admin/login`)
		) {
			const adminResponse = await protectRoute({
				request,
				cookieName: ADMIN_SESSION_COOKIE_NAME,
				expectedRole: "admin",
				redirectTo: `/${pathnameLocale}/admin/login`,
			});

			if (adminResponse) {
				return adminResponse;
			}
		}

		return response;
	}

	// No locale in path — detect and redirect.
	// User's saved preference (`GEO_PREF_COOKIE`) takes priority over geo-detection.
	const geoPref = parseGeoPref(request);
	// Crawler bekommen keine Geo-Personalisierung des Redirect-Ziels: die Root-URL traegt
	// die externe Autoritaet und muss auf dasselbe Ziel zeigen wie das x-default-hreflang
	// (/de/eu). Ohne das leitet Googlebot von US-IPs auf /en/us und der Primaermarkt
	// bekommt keine Startpunkt-Autoritaet.
	const isCrawler = isCrawlerUserAgent(request.headers.get("user-agent"));
	const detectedLocale = isCrawler ? defaultLocale : (geoPref?.locale ?? detectLocale(request));
	const detectedChannel = isCrawler
		? DEFAULT_CHANNEL
		: (geoPref?.channel ?? detectChannel(request, detectedLocale));
	const newUrl = new URL(request.url);

	// If the first segment isn't a valid channel or locale-direct path, insert the
	// default channel to avoid a redirect chain (e.g. /favorites → /de/eu/favorites
	// instead of /favorites → /de/favorites → /de/eu/favorites).
	const firstSeg = pathname.split("/").filter(Boolean)[0];
	const targetPath =
		!firstSeg || (!VALID_CHANNELS.has(firstSeg) && !LOCALE_DIRECT_PATHS.has(firstSeg))
			? `/${detectedLocale}/${detectedChannel}${pathname}`
			: `/${detectedLocale}${pathname}`;
	// Slash-free on purpose: /products/ must not need a second hop through trailingSlashRedirect.
	newUrl.pathname = targetPath.replace(/\/+$/, "");

	// Menschen bekommen ein personalisiertes Ziel (Accept-Language, Land, geo_pref): 307 und
	// no-store, damit kein Browser es dauerhaft cacht und ein späterer Sprachwechsel greift.
	// Crawler behalten den festen 308 auf das x-default-Ziel /de/eu (Owner-Entscheidung E3).
	const localeRedirect = NextResponse.redirect(newUrl, isCrawler ? 308 : 307);
	if (!isCrawler) localeRedirect.headers.set("Cache-Control", "private, no-store");
	return localeRedirect;
}

/** Drop legacy `geo_pref` (pre-`geo_pref_v2`) so clients remove stale JSON after one-time migration. */
function expireLegacyGeoPrefCookie(response: NextResponse) {
	response.cookies.set(LEGACY_GEO_PREF_COOKIE, "", {
		maxAge: 0,
		path: "/",
		sameSite: "lax",
	});
}

export async function middleware(request: NextRequest) {
	// Canary paths answer before every other branch: no redirect, no cookie and no
	// render for scanners. Details: docs/operations/security-canaries.md
	const canary = matchAgentCanary(request.nextUrl.pathname);
	if (canary) return agentCanaryResponse(canary, request.method);

	const requestHeaders = new Headers(request.headers);
	requestHeaders.delete("x-bergdorf-agent-target");
	const cleanRequest = new NextRequest(request, { headers: requestHeaders });
	// This exact API route is explicitly matched below. A direct client cannot
	// forge the internal rewrite target; it receives the route's ordinary 404.
	if (request.nextUrl.pathname === "/api/agent/markdown") {
		return NextResponse.next({ request: { headers: requestHeaders } });
	}

	let response = await handleMiddlewareRequest(cleanRequest);
	let nativeMarkdown = false;
	const pilot = isAgentMarkdownEnabled() && isMarkdownPilotPath(request.nextUrl.pathname);
	if (pilot && response.status === 200 && response.headers.get("x-middleware-next") === "1") {
		const vary =
			response.headers
				.get("Vary")
				?.split(",")
				.map((value) => value.trim()) ?? [];
		if (!vary.some((value) => value.toLowerCase() === "accept")) vary.push("Accept");
		response.headers.set("Vary", vary.join(", "));
		const variantIds = request.nextUrl.searchParams.getAll("variant");
		const targetPath = `${request.nextUrl.pathname}${variantIds.length === 1 ? `?variant=${encodeURIComponent(variantIds[0])}` : ""}`;
		if (
			variantIds.length <= 1 &&
			!isMarkdownRequestExcluded(cleanRequest) &&
			prefersMarkdown(request.headers.get("Accept")) &&
			parseMarkdownTarget(targetPath)
		) {
			requestHeaders.set("x-bergdorf-agent-target", targetPath);
			const internalUrl = new URL("/api/agent/markdown", request.url);
			response = NextResponse.rewrite(internalUrl, { request: { headers: requestHeaders } });
			response.headers.set("Vary", "Accept");
			response.headers.set("Cache-Control", "no-store");
			nativeMarkdown = true;
		}
		// Kein `Link: rel="alternate"` mehr auf der HTML-Antwort. Next liest die
		// Response-Header, die ein Render angefasst hat, von der laufenden ServerResponse
		// in seinen Route-Cache-Eintrag zurueck (`setMetadataHeader` in app-render).
		// Middleware-Header stehen dort bereits, also landete dieser Hinweis im
		// Cache-Eintrag und wurde bei der naechsten Auslieferung erneut oben
		// draufgelegt, pro Revalidierung eine Kopie mehr, bis der Header-Block
		// den nginx-Puffer sprengte (Produktionsausfall 2026-09-19). Die Seiten
		// tragen den Hinweis jetzt als `<link>` im HTML.
	}
	if (
		!nativeMarkdown &&
		!isPublicDiscoveryPath(request.nextUrl.pathname) &&
		request.cookies.get(LEGACY_GEO_PREF_COOKIE)?.value
	) {
		expireLegacyGeoPrefCookie(response);
	}
	// Early NextResponse.next() branches must forward the sanitized headers too.
	if (
		request.headers.has("x-bergdorf-agent-target") &&
		response.headers.has("x-middleware-next") &&
		!response.headers.has("x-middleware-override-headers")
	) {
		const forwarding = NextResponse.next({ request: { headers: requestHeaders } });
		for (const [name, value] of forwarding.headers) {
			if (name.startsWith("x-middleware-")) response.headers.set(name, value);
		}
	}
	if (!nativeMarkdown) applyPublicDiscoveryHeaders(response.headers, request.nextUrl.pathname);
	return response;
}

/**
 * Browser language codes that name a served locale under another code: many
 * Norwegian browsers send the macrolanguage `no`, and Nynorsk falls back to
 * Bokmål until it has its own locale (a valid locale always wins first).
 */
const LANGUAGE_ALIASES: Record<string, string> = { no: "nb", nn: "nb" };

/**
 * Detect the preferred locale from the browser's Accept-Language header,
 * falling back to Cloudflare geo data when Accept-Language is absent or
 * doesn't match any supported locale.
 *
 * Priority:
 * 1. Accept-Language header (browser preference — strongest signal)
 * 2. CF-IPCountry → country-to-locale mapping (fallback for bots/crawlers)
 * 3. Fallback: "en"
 *
 * The result is then refined to a regional locale when the visitor's country selects one for
 * the resolved language: in Mexico `es-US`, `es-419` or `es` become `es-mx`. `en` stays `en`, and
 * an `es-MX` browser keeps `es-mx` in every country through the full-tag match.
 */
function detectLocale(request: NextRequest): string {
	const country = request.headers.get("cf-ipcountry")?.toUpperCase();
	const resolved = detectLanguageOrCountryLocale(request, country);
	return getRegionalLocaleForCountry(country, resolved) ?? resolved;
}

function detectLanguageOrCountryLocale(request: NextRequest, country: string | undefined): string {
	// 1. Accept-Language header (e.g., "de-DE,de;q=0.9,en;q=0.8")
	const acceptLanguage = request.headers.get("accept-language");
	if (acceptLanguage) {
		const preferred = acceptLanguage
			.split(",")
			.map((part) => {
				const [lang, q] = part.trim().split(";q=");
				const tag = lang.trim().toLowerCase();
				return { tag, base: tag.split("-")[0], q: q ? parseFloat(q) : 1 };
			})
			.sort((a, b) => b.q - a.q);

		// Erst das vollstaendige Tag, dann die Basissprache — und beides je Eintrag,
		// damit die Reihenfolge des Browsers gewinnt. Wuerde hier nur die Basis
		// geprueft, faenge `pt` jedes `pt-BR` ab und Brasilien saehe nie pt-br.
		for (const { tag, base } of preferred) {
			if (isValidLocale(tag)) return tag;
			if (isValidLocale(base)) return base;
			const alias = LANGUAGE_ALIASES[base];
			if (alias && isValidLocale(alias)) return alias;
		}
	}

	// 2. Cloudflare country → locale (for bots/crawlers without Accept-Language)
	if (country && country in COUNTRY_TO_LOCALE) {
		const mapped = COUNTRY_TO_LOCALE[country];
		if (isValidLocale(mapped)) return mapped;
	}

	// 3. Fallback
	return "en";
}

/**
 * Detect the storefront channel from Cloudflare's CF-IPCountry header and locale.
 * North/Central/South America + Caribbean → "us" unless locale/home country
 * is it/fi/sv/ro (EU default). Falls back to default when the header is absent.
 */
export function detectChannel(request: NextRequest, locale?: string): string {
	const country = request.headers.get("cf-ipcountry")?.toUpperCase();

	if (locale && EU_CHANNEL_DEFAULT_LOCALES.has(locale)) return DEFAULT_CHANNEL;
	if (country && EU_CHANNEL_DEFAULT_COUNTRIES.has(country)) return DEFAULT_CHANNEL;

	if (country && AMERICAS_COUNTRIES.has(country)) return "us";
	return DEFAULT_CHANNEL;
}

/** Re-export for callers that import the constant from middleware */
export { GEO_PREF_COOKIE };

/**
 * Parse `GEO_PREF_COOKIE` (`geo_pref_v2`). Returns validated locale + channel or null.
 */
function parseGeoPref(request: NextRequest): { locale: string; channel: string } | null {
	const raw = request.cookies.get(GEO_PREF_COOKIE)?.value;
	const parsed = parseGeoPrefCookieValue(raw);
	if (!parsed) return null;
	if (!VALID_CHANNELS.has(parsed.channel)) return null;
	return { locale: parsed.locale, channel: parsed.channel };
}

/** Login, onboarding, and password recovery — no session required */
function isPublicAffiliatePath(pathname: string, locale: string) {
	const base = `/${locale}/affiliate`;
	return (
		pathname.startsWith(`${base}/login`) ||
		pathname.startsWith(`${base}/setup`) ||
		pathname.startsWith(`${base}/forgot-password`) ||
		pathname.startsWith(`${base}/reset-password`)
	);
}

async function protectRoute(params: {
	request: NextRequest;
	cookieName: string;
	expectedRole: "affiliate" | "admin";
	redirectTo: string;
}) {
	const secret = process.env.AFFILIATE_JWT_SECRET;
	if (!secret) {
		return NextResponse.redirect(new URL(params.redirectTo, params.request.url), 307);
	}

	const token = params.request.cookies.get(params.cookieName)?.value;
	if (!token) {
		return NextResponse.redirect(new URL(params.redirectTo, params.request.url), 307);
	}

	const session = await verifyEdgeSessionToken(token, secret);
	if (!session || session.role !== params.expectedRole) {
		return NextResponse.redirect(new URL(params.redirectTo, params.request.url), 307);
	}

	return null;
}

function buildCleanPath(url: NextRequest["nextUrl"]) {
	const cleanUrl = url.clone();
	cleanUrl.searchParams.delete("ref");
	for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
		cleanUrl.searchParams.delete(key);
	}
	const search = cleanUrl.searchParams.toString();
	return `${cleanUrl.pathname}${search ? `?${search}` : ""}`;
}

export const config = {
	matcher: [
		// Strip forged internal targets on direct calls; other API routes stay excluded.
		"/api/agent/markdown",
		// Match paths except _next, api, common static assets — keep `.xml`/`.xsl` matched so the explicit `/sitemap.xml` + `/sitemap.xsl` bypass above runs; the XSL was removed on 2026-10-25, so `/sitemap.xsl` answers 404 (`.txt` is NOT excluded — deliberately included so `/robots.txt`, `/llms.txt`, and the IndexNow key file reach the middleware).
		// `/.well-known/*` is carved out as its own alternative (ahead of the extension exclusions) so
		// probes with an otherwise-excluded extension (e.g. `assetlinks.json`) still reach the
		// middleware and get a defined 404 instead of silently falling through to `/_not-found`.
		"/((?:/?\\.well-known/.*)|(?!_next|api|favicon|apple-icon|opengraph-image|site\\.webmanifest|.*\\.(?:ico|png|jpe?g|gif|webp|svg|css|js|mjs|map|woff2?|ttf|eot|json|pdf|webmanifest)).*)",
	],
};
